import React from "react";
import { useParams } from "react-router-dom";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import ViewPage from "../../components/view/ViewPage";
import ViewFieldCard from "../../components/view/fields/ViewFieldCard";
import ViewSubCard from "../../components/view/fields/ViewSubCard";
import ViewTextField from "../../components/view/fields/ViewTextField";
import ViewRangeField from "../../components/view/fields/ViewRangeField";
import ViewLinksField from "../../components/view/fields/ViewLinksField";
import ViewRichTextField from "../../components/view/fields/ViewRichTextField";
import ViewMultiSelectionRow from "../../components/view/fields/ViewMultiSelectionRow";
import ViewLanguagesField from "../../components/view/fields/ViewLanguagesField";
import { Person, PersonNameInternalEvidence } from "../../types/person";
import usePersonController from "../../controllers/personController";
import EntityReferenceComponent from "../../components/entitiesReferences/EntityReferenceComponent";
import { useEntityHandlerContext } from "../../controllers/entityController";
import PersonAssociatedVersionsViewSection from "./sections/view/PersonAssociatedVersionsViewSection";
import PersonConnectionsViewSection from "./sections/view/PersonConnectionsViewSection";
import PersonImpliesConnectionsViewSection from "./sections/view/PersonImpliesConnectionsViewSection";
import PlaceOfPersonViewSection from "./sections/view/PlaceOfPersonViewSection";

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const EvidenceDetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}> = ({ label, value, fullWidth = false }) => (
  <Grid size={fullWidth ? { xs: 12 } : { xs: 12, sm: 6 }}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box>{value}</Box>
    </Box>
  </Grid>
);

const PersonEvidenceSubCard: React.FC<{
  title: string;
  evidence?: PersonNameInternalEvidence | null;
}> = ({ title, evidence }) => (
  <ViewSubCard variant="nested" title={title}>
    <EvidenceDetailRow
      label="Name Attribute Pre"
      value={
        <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
          {displayText(evidence?.attribute_pre)}
        </Typography>
      }
    />
    <EvidenceDetailRow
      label="Name"
      value={
        <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
          {displayText(evidence?.name)}
        </Typography>
      }
    />
    <EvidenceDetailRow
      label="Name Attribute Post"
      value={
        <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
          {displayText(evidence?.attribute_post)}
        </Typography>
      }
    />
    <EvidenceDetailRow
      label="Name Reconstruction Status Type"
      value={
        <Typography variant="body1">
          {displayText(evidence?.reconstruction_status_type)}
        </Typography>
      }
    />
    <EvidenceDetailRow
      label="Status Type"
      value={
        <Typography variant="body1">
          {displayText(evidence?.status_type)}
        </Typography>
      }
    />
  </ViewSubCard>
);

interface NamesSectionProps {
  person: Person;
}

const NamesSection: React.FC<NamesSectionProps> = ({ person }) => {
  const designations = Array.isArray(person?.other_designations)
    ? person.other_designations
    : [];

  return (
    <>
      <PersonEvidenceSubCard
        title="Name Internal Evidence"
        evidence={person?.name_internal_evidence}
      />
      {designations.length === 0 ? (
        <Grid size={{ xs: 12 }}>
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Other Designations
            </Typography>
            <Typography variant="body1">N/A</Typography>
          </Box>
        </Grid>
      ) : (
        designations.map((designation, index) => (
          <PersonEvidenceSubCard
            key={index}
            title={`Other Designation ${index + 1}`}
            evidence={designation}
          />
        ))
      )}
    </>
  );
};

const PlacesSection: React.FC<{ person: Person }> = ({ person }) => {
  return (
    <>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Place of Birth
          </Typography>
          {person?.place_of_birth ? (
            <EntityReferenceComponent entity={person.place_of_birth} linkMode="view" />
          ) : (
            <Typography variant="body1">N/A</Typography>
          )}
        </Box>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Region of Activity
          </Typography>
          {person?.region_of_activity ? (
            <EntityReferenceComponent
              entity={person.region_of_activity}
              linkMode="view"
            />
          ) : (
            <Typography variant="body1">N/A</Typography>
          )}
        </Box>
      </Grid>
    </>
  );
};

const ViewPersonPage: React.FC = () => {
  const { _id } = useParams<{ _id: string }>();

  if (!_id) {
    throw new Error("Person ID is required for viewing.");
  }

  const controller = usePersonController(_id);

  return (
    <ViewPage<Person>
      controller={controller}
      sections={(person) => [
        <PersonAssociatedVersionsViewSection person={person} />,
        <PersonConnectionsViewSection person={person} />,
        <PersonImpliesConnectionsViewSection person={person} />,
        <PlaceOfPersonViewSection person={person} />,
      ]}
    >
      <ViewFieldCard title="Basic Information">
        <ViewTextField name="published_id" label="Intellexus ID" />
        <ViewTextField name="origin" label="Origin" />
        <ViewTextField name="gender" label="Gender" />
        <ViewTextField name="primary_title_type" label="Primary Title Type" />
      </ViewFieldCard>

      <ViewFieldCard title="Names">
        <PersonNamesContent />
      </ViewFieldCard>

      <ViewFieldCard title="Dates">
        <ViewRangeField name="floruit" label="Floruit" />
        <ViewRangeField name="date_of_birth" label="Date of Birth" />
        <ViewRangeField name="date_of_death" label="Date of Death" />
      </ViewFieldCard>

      <ViewFieldCard title="Places">
        <PersonPlacesContent />
      </ViewFieldCard>

      <ViewFieldCard title="Affiliated Traditions">
        <PersonAffiliatedTraditionsContent />
      </ViewFieldCard>

      <ViewFieldCard title="Languages">
        <ViewLanguagesField name="languages" rowLabel="Language" />
      </ViewFieldCard>

      <ViewFieldCard title="Links">
        <ViewLinksField
          name="link_to_other_databases"
          label="Links to Other Databases"
        />
      </ViewFieldCard>

      <ViewFieldCard title="Notes">
        <ViewRichTextField name="notes" />
      </ViewFieldCard>
    </ViewPage>
  );
};

const PersonNamesContent: React.FC = () => {
  const { methods } = useEntityHandlerContext();
  const person = methods.getValues() as Person;
  return <NamesSection person={person} />;
};

const PersonPlacesContent: React.FC = () => {
  const { methods } = useEntityHandlerContext();
  const person = methods.getValues() as Person;
  return <PlacesSection person={person} />;
};

const PersonAffiliatedTraditionsContent: React.FC = () => {
  const { methods } = useEntityHandlerContext();
  const person = methods.getValues() as Person;
  return <ViewMultiSelectionRow values={person?.affiliated_traditions ?? []} />;
};

export default ViewPersonPage;
